<script setup lang="ts">
const props = defineProps<{
  controller: ReturnType<typeof useModal>;
}>();

const { cartItems, totalPrice, isEmpty, shippingTotal, subtotal, cart } =
  useCart();
const localePath = useLocalePath();
const { t } = useI18n();

const { formatLink } = useInternationalization(localePath);

const shippingCosts = computed(() => cart.value?.deliveries || []);

const freeShippingThresholdValue = 80;

const freeShippingThreshold = computed(() => {
  const prices = shippingCosts.value?.[0]?.shippingMethod?.prices || [];

  // Find the shipping price with gross = 0
  const freeShippingPrice = prices.find((price) => {
    return price.currencyPrice?.some((cp) => cp.gross === 0);
  });

  return freeShippingPrice?.quantityStart ?? freeShippingThresholdValue;
});

const percent = computed(() => {
  if (!freeShippingThreshold.value) return 0;
  const percentage = (subtotal.value / freeShippingThreshold.value) * 100;
  return Math.min(100, percentage);
});

const remainingAmount = computed(() => {
  return freeShippingThreshold.value
    ? Math.max(
        0,
        Math.round((freeShippingThreshold.value - subtotal.value) * 100) / 100
      )
    : 0;
});
</script>

<template>
  <LayoutSidebar :controller="props.controller" side="right">
    <aside class="flex-1 overflow-y-auto py-6 px-4 sm:px-6">
      <div class="flex items-start justify-between">
        <h2
          id="slide-over-title"
          class="text-lg font-medium text-secondary-900 py-0"
        >
          {{ $t("sideCart.title") }}
        </h2>
        <div class="ml-3 flex h-7 items-center">
          <button
            type="button"
            class="-m-2 p-2 bg-transparent text-secondary-400 hover:text-secondary-500"
            data-testid="cart-close-button"
            @click="props.controller.close"
          >
            <span class="sr-only">{{ $t("sideCart.closePanel") }}</span>
            <div class="w-6 h-6 i-carbon-close" />
          </button>
        </div>
      </div>

      <div class="mt-1">
        <div class="flow-root">
          <div class="text-left mb-10" v-if="!isEmpty">
            <div
              class="mt-2 relative max-w-full w-[560px] m-auto h-4 bg-light-grey"
            >
              <div
                class="h-full absolute start-0 top-0 bg-red-bg rounded-full"
                :style="'width: ' + percent + '%;'"
              ></div>
            </div>
            <p
              :class="{ hidden: percent >= 100 }"
              class="font-bold text-red-bg"
            >
              {{ $t("cart.only") }}
              <SharedPrice
                :value="remainingAmount"
                data-testid="cart-subtotal"
              />
              {{ $t("cart.until") }}
            </p>
            <p :class="{ hidden: percent < 100 }" class="font-bold text-red-bg">
              {{ $t("cart.freeShipping") }}
            </p>
          </div>
          <ul
            v-if="!isEmpty"
            role="list"
            class="-my-6 px-0 divide-y divide-secondary-200"
          >
            <li
              v-for="cartItem in cartItems"
              :key="cartItem.id"
              class="flex py-6"
            >
              <CheckoutCartItem :cart-item="cartItem" />
            </li>
          </ul>
          <div
            v-else
            class="flex flex-col items-center justify-center py-12 space-y-4"
          >
            <img
              src="assets/images/shopping-cart-black.svg"
              alt="cart-icon"
              class="w-16 h-16"
            />
            <h3 class="text-2xl text-secondary-700 font-bold">
              {{ $t("sideCart.empty") }}
            </h3>
            <p
              class="text-sm text-secondary-500 cursor-pointer"
              @click="props.controller.close"
            >
              {{ $t("sideCart.startShopping") }}
            </p>
          </div>
        </div>
      </div>
    </aside>

    <aside
      aria-label="Side Cart Links"
      class="border-t border-secondary-200 py-6 px-4 sm:px-6"
    >
      <div
        v-if="!isEmpty"
        class="flex justify-between items-center text-secondary-900 font-medium"
      >
        <span>{{ $t("cart.shippingEstimate") }}</span>
        <span>
          <SharedPrice :value="shippingTotal" data-testid="cart-subtotal" />
        </span>
      </div>
      <div
        v-if="!isEmpty"
        class="flex justify-between text-base font-medium text-secondary-900 mt-4"
      >
        <p>{{ $t("sideCart.subtotal") }}</p>
        <SharedPrice :value="totalPrice" data-testid="cart-subtotal" />
      </div>

      <p v-if="!isEmpty" class="mt-8 text-sm text-secondary-500">
        {{ $t("sideCart.shippingNote") }}
      </p>
      <div class="mt-4">
        <NuxtLink
          class="block btn-default-px-9 btn-default btn-black-bg"
          :class="{
            '!text-[#bcc1c7] bg-[#f9f9f9] cursor-default': isEmpty,
            'text-white bg-primary hover:bg-dark': !isEmpty,
          }"
          :to="formatLink(isEmpty ? '' : '/checkout')"
          data-testid="cart-checkout-link"
        >
          {{ $t("sideCart.checkout") }}
        </NuxtLink>

        <NuxtLink
          class="btn-default btn-transprent-bg w-full btn-w-icon justify-center mt-3"
          :to="formatLink(`/checkout/cart`)"
          data-testid="cart-checkout-shopping-cart"
          @click="props.controller.close"
        >
          {{ $t("sideCart.goToCart") }}
        </NuxtLink>
      </div>
      <div class="mt-6 flex justify-center text-center text-sm text-dark">
        <p>
          {{ $t("sideCart.or") }}
          <button
            type="button"
            class="font-medium bg-transparent"
            data-testid="cart-continue-button"
            @click="props.controller.close"
          >
            {{ $t("sideCart.continueShopping")
            }}<span aria-hidden="true"> &rarr;</span>
          </button>
        </p>
      </div>
    </aside>
  </LayoutSidebar>
</template>
